import { Injectable, inject } from "@angular/core";
import { AnalyticsService } from "./analytics.service";
import { Router } from "@angular/router";

/**
 * Helper service for redirecting links to a configurable target
 * _blank by default
 */
@Injectable({
  providedIn: "root",
})
export class LinkNavigationService {
  private readonly analytics = inject(AnalyticsService);
  private readonly router = inject(Router);

  public target = "_blank";
  private readonly origin: string;

  constructor() {
    // baseUrl
    this.origin = window.location.origin;
  }

  /**
   * Checks if element is a valid Anchor tag with an href defined
   * @param el
   */
  public isValidLinkElement(el: HTMLElement): boolean {
    return el.tagName === "A" && el.hasAttribute("href");
  }

  /**
   * Returns true if the event was handled
   * @param e MouseEvent
   */
  public handleClick(e: MouseEvent | KeyboardEvent): boolean {
    // Guard against non-browser hosts (SSR): `typeof` is the honest existence
    // check; `!window` reads as always-false because lib.dom types it non-null.
    if (typeof window === "undefined") {
      return false;
    }

    // if the event is a mouse event, check if it is a left click
    if (e instanceof MouseEvent && e.button !== 0) {
      return false;
    }

    const el = e.target as HTMLAnchorElement;
    let href = el.href;

    // disable link handling if inside editable region
    if (el.closest('[contenteditable="true"]')) {
      return false;
    }

    // use default behaviour if no href defined
    if (!this.isValidLinkElement(el)) {
      return false;
    }

    // prevent default before we do anything else
    e.preventDefault();
    e.stopPropagation();

    if (href.startsWith(this.origin)) {
      href = href.substring(this.origin.length); // strip off origin but leave initial slash
    }

    if (href.startsWith("/")) {
      void this.router.navigate([href]);
      return true;
    }

    this.analytics.trackOutboundLink(el.href);

    // handle link manually
    window.open(el.href, this.target);
    return true;
  }
}
